/**
 * RegionalDetail Page - Complete Regional Form information
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Heart, ArrowLeft, 
  MapPin, ArrowRight, TrendingUp
} from 'lucide-react';
import { usePokemon } from '../hooks/usePokemon';
import { useApp } from '../context/AppContext';
import { TypeBadgeGroup } from '../components/pokedex/TypeBadge';
import { TypeCalculator } from '../components/pokedex/TypeCalculator';
import { MovesList } from '../components/pokedex/MovesList';
import { EvolutionChain } from '../components/pokedex/EvolutionChain';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { REGIONAL_FORMS, getRegionalApiId, REGION_COLORS } from '../data/regionalForms';
import { cn } from '../lib/utils';

// Stat colors
const STAT_COLORS = {
  hp: 'bg-red-500',
  attack: 'bg-orange-500',
  defense: 'bg-yellow-500',
  'special-attack': 'bg-blue-500',
  'special-defense': 'bg-green-500',
  speed: 'bg-pink-500'
};

const STAT_NAMES = {
  hp: { en: 'HP', es: 'PS' },
  attack: { en: 'Attack', es: 'Ataque' },
  defense: { en: 'Defense', es: 'Defensa' },
  'special-attack': { en: 'Sp. Atk', es: 'At. Esp.' },
  'special-defense': { en: 'Sp. Def', es: 'Def. Esp.' },
  speed: { en: 'Speed', es: 'Velocidad' }
};

// Stat bar component
function StatBar({ name, value, maxValue = 255, language }) {
  const percentage = (value / maxValue) * 100;
  const label = STAT_NAMES[name]?.[language] || name;
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-16 sm:w-20 truncate">{label}</span>
      <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={cn('h-full rounded-full', STAT_COLORS[name] || 'bg-violet-500')}
        />
      </div>
      <span className="text-sm font-mono w-8 text-right font-semibold">{value}</span>
    </div>
  );
}

export default function RegionalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isFavorite, toggleFavorite, language } = useApp();
  const { getPokemon, getPokemonSpecies, getLocalizedName, getLocalizedFlavorText, getGeneration } = usePokemon();
  
  const [regionalPokemon, setRegionalPokemon] = useState(null);
  const [regionalSpecies, setRegionalSpecies] = useState(null);
  const [basePokemon, setBasePokemon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const regionalId = parseInt(id);
  
  // Find the regional form data
  const regionalData = useMemo(() => {
    return REGIONAL_FORMS.find(r => r.id === regionalId);
  }, [regionalId]);
  
  // Get all regionals for navigation
  const allRegionals = useMemo(() => REGIONAL_FORMS, []);
  const currentIndex = allRegionals.findIndex(r => r.id === regionalId);
  
  const favorite = isFavorite(regionalId);
  
  // Get API ID for the regional form
  const apiId = regionalData ? getRegionalApiId(regionalData.apiName) : null;
  
  // Fetch regional pokemon data
  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      if (!regionalData) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setImageLoaded(false);
      
      // Fetch regional form data using the API ID
      const [pokemonData, speciesData, baseData] = await Promise.all([
        apiId ? getPokemon(apiId) : null,
        getPokemonSpecies(regionalData.baseId),
        getPokemon(regionalData.baseId)
      ]);
      
      if (mounted) {
        setRegionalPokemon(pokemonData);
        setRegionalSpecies(speciesData);
        setBasePokemon(baseData);
        setLoading(false);
      }
    };
    
    fetchData();
    return () => { mounted = false; };
  }, [regionalData, apiId, getPokemon, getPokemonSpecies]);
  
  // Get sprite URL
  const spriteUrl = useMemo(() => {
    if (!regionalData) return '';
    
    if (apiId) {
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${apiId}.png`;
    }
    // Fallback to base Pokemon sprite
    return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${regionalData.baseId}.png`;
  }, [regionalData, apiId]);
  
  // Get types from regional data (prioritize API data if available)
  const types = regionalPokemon?.types?.map(t => t.type.name) || regionalData?.types || [];
  
  // Get stats from regional Pokemon (API data if available)
  const stats = regionalPokemon?.stats || basePokemon?.stats || [];
  const bst = stats.reduce((sum, s) => sum + s.base_stat, 0);
  
  // Get abilities from regional Pokemon
  const abilities = regionalPokemon?.abilities || basePokemon?.abilities || [];
  
  // Get localized data
  const description = regionalSpecies ? getLocalizedFlavorText(regionalSpecies.flavor_text_entries) : '';
  const generation = regionalData ? getGeneration(regionalData.baseId) : 1;
  
  // Region info
  const regionInfo = regionalData ? REGION_COLORS[regionalData.region] : null;
  const regionLabel = regionInfo?.label[language] || regionalData?.region;
  
  // Navigation
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allRegionals.length - 1;
  
  const goToPrevious = () => {
    if (hasPrevious) navigate(`/regional/${allRegionals[currentIndex - 1].id}`);
  };
  
  const goToNext = () => {
    if (hasNext) navigate(`/regional/${allRegionals[currentIndex + 1].id}`);
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);
  
  // Texts
  const texts = {
    region: language === 'es' ? 'Región' : 'Region',
    stats: language === 'es' ? 'Estadísticas' : 'Stats',
    abilities: language === 'es' ? 'Habilidades' : 'Abilities',
    hiddenAbility: language === 'es' ? 'Habilidad oculta' : 'Hidden Ability',
    total: language === 'es' ? 'Total' : 'Total',
    viewBase: language === 'es' ? 'Ver forma original' : 'View original form',
    weakRes: language === 'es' ? 'Debilidades y Resistencias' : 'Weaknesses & Resistances',
    regionalForm: language === 'es' ? 'Forma Regional' : 'Regional Form'
  };
  
  if (loading) {
    return <RegionalDetailSkeleton />;
  }
  
  if (!regionalData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-muted-foreground mb-4">{t('error')}</p>
          <Button onClick={() => navigate('/')}>{t('home')}</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" data-testid="regional-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button and navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('home')}
            </Button>
          </Link>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              disabled={!hasPrevious}
              data-testid="prev-regional-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[80px] text-center flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" style={{ color: regionInfo?.bg?.replace('bg-', '') }} />
              {currentIndex + 1}/{allRegionals.length}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              disabled={!hasNext}
              data-testid="next-regional-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left column - Image and basic info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-5"
          >
            <div className={cn(
              "bg-card rounded-3xl border-2 shadow-lg overflow-hidden",
              regionalData.region === 'alola' && "border-yellow-500/50",
              regionalData.region === 'galar' && "border-rose-500/50",
              regionalData.region === 'hisui' && "border-cyan-500/50",
              regionalData.region === 'paldea' && "border-violet-500/50"
            )}>
              {/* Region badge header */}
              <div className={cn("py-2 px-4 flex items-center justify-between", regionInfo?.bg)}>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-sm font-bold text-white uppercase tracking-wider">{regionLabel}</span>
                </div>
                <span className="text-xs text-white/80">{texts.regionalForm}</span>
              </div>
              
              {/* Image section */}
              <div className={cn(
                "relative p-8",
                regionalData.region === 'alola' && "bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/20",
                regionalData.region === 'galar' && "bg-gradient-to-br from-rose-100 to-pink-100 dark:from-rose-900/20 dark:to-pink-900/20",
                regionalData.region === 'hisui' && "bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/20 dark:to-blue-900/20",
                regionalData.region === 'paldea' && "bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/20 dark:to-purple-900/20"
              )}>
                {/* ID watermark */}
                <span className="absolute top-4 left-4 text-6xl font-black text-black/5 dark:text-white/5 select-none">
                  #{String(regionalData.baseId).padStart(3, '0')}
                </span>
                
                {/* Favorite button */}
                <button
                  onClick={() => toggleFavorite(regionalId)}
                  data-testid="regional-detail-favorite-btn"
                  className="absolute top-4 right-4 p-3 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform z-10"
                >
                  <Heart 
                    className={`w-6 h-6 transition-colors ${
                      favorite 
                        ? 'fill-rose-500 text-rose-500' 
                        : 'text-muted-foreground hover:text-rose-500'
                    }`}
                  />
                </button>
                
                {/* Pokemon image */}
                <div className="relative flex items-center justify-center h-64 sm:h-80">
                  {!imageLoaded && (
                    <Skeleton className="absolute w-48 h-48 rounded-full" />
                  )}
                  <AnimatePresence mode="wait">
                    <motion.img
                      src={spriteUrl}
                      alt={regionalData.name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: imageLoaded ? 1 : 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      onLoad={() => setImageLoaded(true)}
                      onError={(e) => {
                        e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${regionalData.baseId}.png`;
                        setImageLoaded(true);
                      }}
                      className="w-48 h-48 sm:w-64 sm:h-64 object-contain drop-shadow-2xl"
                    />
                  </AnimatePresence>
                </div>
              </div>
              
              {/* Basic info */}
              <div className="p-6 space-y-4">
                <div>
                  <p className={cn(
                    "text-sm font-bold uppercase tracking-widest mb-1 flex items-center gap-1",
                    regionalData.region === 'alola' && "text-yellow-600 dark:text-yellow-400",
                    regionalData.region === 'galar' && "text-rose-600 dark:text-rose-400",
                    regionalData.region === 'hisui' && "text-cyan-600 dark:text-cyan-400",
                    regionalData.region === 'paldea' && "text-violet-600 dark:text-violet-400"
                  )}>
                    <MapPin className="w-3 h-3" />
                    #{String(regionalData.baseId).padStart(3, '0')} • {t('generation')} {generation}
                  </p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
                    {regionalData.name}
                  </h1>
                </div>
                
                {/* Types */}
                <TypeBadgeGroup types={types} size="md" />
                
                {/* Description */}
                {description && (
                  <div className="pt-2">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {description}
                    </p>
                  </div>
                )}
                
                {/* View base Pokemon link */}
                <Link to={`/pokemon/${regionalData.baseId}`}>
                  <Button variant="outline" className="w-full mt-2" data-testid="view-base-btn">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    {texts.viewBase}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
          
          {/* Right column - Tabs with detailed info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="bg-card rounded-3xl border border-border shadow-lg p-4 sm:p-6">
              <Tabs defaultValue="stats" className="w-full">
                <TabsList className="flex flex-wrap w-full mb-6 h-auto gap-1">
                  <TabsTrigger value="stats" data-testid="tab-stats" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('stats')}</TabsTrigger>
                  <TabsTrigger value="abilities" data-testid="tab-abilities" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('abilities')}</TabsTrigger>
                  <TabsTrigger value="moves" data-testid="tab-moves" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('moves')}</TabsTrigger>
                  <TabsTrigger value="evolution" data-testid="tab-evolution" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('evolution')}</TabsTrigger>
                </TabsList>

                <TabsContent value="stats" className="space-y-6">
                  <div className="space-y-3">
                    {stats.map((stat) => (
                      <StatBar
                        key={stat.stat.name}
                        name={stat.stat.name}
                        value={stat.base_stat}
                        language={language}
                      />
                    ))}
                  </div>

                  {/* Total BST */}
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground">{texts.total}</span>
                      <span className="font-bold text-xl text-foreground">{bst}</span>
                    </div>
                  </div>

                  {/* Type Weaknesses */}
                  <div className="pt-4 border-t border-border">
                    <h3 className="text-lg font-bold text-foreground mb-4">
                      {texts.weakRes}
                    </h3>
                    <TypeCalculator types={types} />
                  </div>
                </TabsContent>

                <TabsContent value="abilities">
                  {abilities.length > 0 ? (
                    <div className="space-y-2">
                      {abilities.map((ability, idx) => (
                        <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm font-semibold capitalize">
                            {ability.ability.name.replace(/-/g, ' ')}
                          </p>
                          {ability.is_hidden && (
                            <p className="text-xs text-violet-600 dark:text-violet-400 italic">
                              {texts.hiddenAbility}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('noAbilities') || 'No abilities'}</p>
                  )}
                </TabsContent>

                <TabsContent value="moves">
                  {regionalPokemon?.moves ? (
                    <MovesList moves={regionalPokemon.moves} />
                  ) : basePokemon?.moves ? (
                    <MovesList moves={basePokemon.moves} />
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('noMoves') || 'No moves'}</p>
                  )}
                </TabsContent>

                <TabsContent value="evolution">
                  {regionalSpecies ? (
                    <EvolutionChain speciesData={regionalSpecies} />
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('noEvolution') || 'No evolution data'}</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function RegionalDetailSkeleton() {
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-9 w-24" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-9" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-9 w-9" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="bg-card rounded-3xl border-2 border-cyan-500/30 overflow-hidden">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-80 w-full" />
              <div className="p-6 space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-20 rounded-full" />
                  <Skeleton className="h-7 w-20 rounded-full" />
                </div>
                <Skeleton className="h-16 w-full rounded-xl" />
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-card rounded-3xl border border-border p-6">
              <Skeleton className="h-8 w-32 mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            </div>
            <div className="bg-card rounded-3xl border border-border p-6">
              <Skeleton className="h-8 w-48 mb-4" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
